import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import type { AnswerAppearance } from '../lib/customQuestionDraft.js';
import styles from './AnswerAppearanceEditor.module.css';

/** The colour an `<input type="color">` shows before the organizer picks one — it has no "unset" state of its own. */
const DEFAULT_SWATCH = '#64748b';

/**
 * One answer's card appearance: colour, emoji, image (PLAN.md Phase 10.11).
 *
 * Only ever rendered when a question's display format is `card`, since that
 * is the only layout that draws any of the three — a list answer is a plain
 * labelled row. Shared by the two places an organizer can set them: the
 * question block editor at creation time, and the rules panel of
 * `EditQuestionResultsScreen` afterwards.
 *
 * The image uploads the moment a file is chosen rather than at submit: at
 * creation time the question (and the circle, and the bolão) don't exist
 * yet, so there is nothing to attach a deferred upload to. What comes back
 * is a URL, held like any other field.
 */
export default function AnswerAppearanceEditor({
  value,
  onChange,
}: {
  value: AnswerAppearance;
  onChange: (patch: Partial<AnswerAppearance>) => void;
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);

  async function upload(file: File) {
    setUploading(true);
    setUploadFailed(false);
    try {
      const { url } = await api.uploadQuestionImage(file);
      onChange({ imageUrl: url });
    } catch {
      setUploadFailed(true);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className={styles.row}>
      <label className={styles.field}>
        {t('customCompetition.answerColorLabel')}
        <span className={styles.controls}>
          <input
            type="color"
            className={styles.colorInput}
            value={value.color || DEFAULT_SWATCH}
            onChange={(e) => onChange({ color: e.target.value })}
          />
          {value.color ? (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => onChange({ color: '' })}
            >
              {t('common.remove')}
            </button>
          ) : null}
        </span>
      </label>

      <label className={styles.field}>
        {t('customCompetition.answerEmojiLabel')}
        <input
          className={`input ${styles.emojiInput}`}
          value={value.emoji}
          maxLength={16}
          placeholder={t('customCompetition.answerEmojiPlaceholder')}
          onChange={(e) => onChange({ emoji: e.target.value })}
        />
      </label>

      <div className={styles.field}>
        <span>{t('customCompetition.answerImageLabel')}</span>
        <span className={styles.controls}>
          {value.imageUrl ? <img src={value.imageUrl} alt="" className={styles.preview} /> : null}
          {/* The real file input is unstylable across browsers, so it's
              hidden inside this label and the label is what gets styled —
              clicking it still opens the picker. */}
          <label className={styles.fileButton}>
            {uploading
              ? t('customCompetition.answerImageUploading')
              : t('customCompetition.answerImageChoose')}
            <input
              type="file"
              accept="image/*"
              className={styles.fileInput}
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Clear the input so re-picking the same file still fires.
                e.target.value = '';
                if (file) void upload(file);
              }}
            />
          </label>
          {value.imageUrl ? (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => onChange({ imageUrl: '' })}
            >
              {t('common.remove')}
            </button>
          ) : null}
        </span>
        {uploadFailed ? (
          <span className={styles.uploadError}>{t('customCompetition.answerImageFailed')}</span>
        ) : null}
      </div>
    </div>
  );
}
